import { format } from "date-fns"

export type ExportColumn = { id: string, header: string }
export type ExportFormat = "csv" | "xlsx" | "md"
export type ExportScope = "page" | "all" | "selected"

export interface ExportOptions {
  format: ExportFormat
  scope: ExportScope
  columns: ExportColumn[]
  maxRows: number
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)/

// Same shape as DataTable.cellRender.date so the file matches what's on screen
function formatDate(value: Date) {
  return format(value, "MM/dd/yyyy HH:mm:ss")
}

// Raw cell value normalized for a spreadsheet. On xlsx numbers and booleans stay
// native so Excel types them instead of treating them as text.
export function formatExportValue(value: any, target: ExportFormat): string | number | boolean {
  if (value === null || value === undefined) return ""
  if (value instanceof Date) return formatDate(value)

  if (typeof value === "string") {
    if (ISO_DATE.test(value)) {
      const d = new Date(value)
      if (!isNaN(d.getTime())) return formatDate(d)
    }
    return value
  }

  if (typeof value === "number") return target === "xlsx" ? value : String(value)
  if (typeof value === "boolean") return target === "xlsx" ? value : String(value)

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function toMatrix(rows: any[], columns: ExportColumn[], target: ExportFormat) {
  const header = columns.map((c) => c.header)
  const body = rows.map((row) => columns.map((c) => formatExportValue(row?.[c.id], target)))
  return [header, ...body]
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function escapeCSV(value: string | number | boolean) {
  const str = String(value)
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) return `"${str.replace(/"/g, '""')}"`
  return str
}

export function exportRowsToCSV(rows: any[], columns: ExportColumn[], filename: string) {
  const content = toMatrix(rows, columns, "csv").map((row) => row.map(escapeCSV).join(",")).join("\n")
  // UTF-8 BOM so Excel detects the encoding and renders accented chars correctly
  const blob = new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" })
  downloadBlob(blob, `${filename}.csv`)
}

function escapeMarkdown(value: string | number | boolean) {
  // Pipes break the cell and newlines break the row, so both need neutralizing
  return String(value).replace(/\|/g, "\\|").replace(/\r?\n/g, "<br>")
}

export function exportRowsToMarkdown(rows: any[], columns: ExportColumn[], filename: string) {
  const [header, ...body] = toMatrix(rows, columns, "md")
  const lines = [
    `| ${header.map(escapeMarkdown).join(" | ")} |`,
    `| ${header.map(() => "---").join(" | ")} |`,
    ...body.map((row) => `| ${row.map(escapeMarkdown).join(" | ")} |`),
  ]
  const blob = new Blob([lines.join("\n") + "\n"], { type: "text/markdown;charset=utf-8;" })
  downloadBlob(blob, `${filename}.md`)
}

export async function exportRowsToXLSX(rows: any[], columns: ExportColumn[], filename: string) {
  // Dynamic import: the xlsx bundle is ~900KB and only needed when exporting to Excel
  const XLSX = await import("xlsx")
  const sheet = XLSX.utils.aoa_to_sheet(toMatrix(rows, columns, "xlsx"))
  const book = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(book, sheet, "Export")
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" })
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
  downloadBlob(blob, `${filename}.xlsx`)
}

export async function exportRows(rows: any[], options: ExportOptions, filename: string) {
  if (options.format === "xlsx") await exportRowsToXLSX(rows, options.columns, filename)
  else if (options.format === "md") exportRowsToMarkdown(rows, options.columns, filename)
  else exportRowsToCSV(rows, options.columns, filename)
}

export function sanitizeFileName(name: string) {
  return name.replace(/[^a-z0-9._-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "export"
}
